import { useEffect } from 'react';
import type { Product, BranchAvailabilityGroup } from '../../../types/product';
import { groupAvailabilityByBranch } from '../../../types/product';
import { API_CONFIG } from '../../../services/config';
import { fullProductName, productPageUrl } from '../../../utils/productName';

interface Props {
  product: Product;
  onClose: () => void;
}

export default function AvailabilityModal({ product, onClose }: Props) {
  // Close on Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const branches = groupAvailabilityByBranch(product.availability);
  const totalStock = product.availability.reduce((sum, a) => sum + a.quantity, 0);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl z-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-zinc-100">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-red-500 mb-0.5">
              Disponibilidad
            </p>
            <h3 className="text-zinc-900 font-extrabold text-sm sm:text-base leading-snug line-clamp-2">
              {product.name}
            </h3>
            {product.brand && (
              <p className="text-zinc-400 text-xs mt-0.5">{product.brand}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-zinc-100 hover:bg-zinc-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-sm text-zinc-600" />
          </button>
        </div>

        {/* Summary strip */}
        <div className="flex items-center justify-between px-5 py-3 bg-zinc-50 border-b border-zinc-100">
          <div className="flex items-center gap-2">
            <span
              className={`w-2 h-2 rounded-full flex-shrink-0 ${
                totalStock > 0 ? 'bg-emerald-500' : 'bg-red-500'
              }`}
            />
            <span className="text-xs font-semibold text-zinc-700">
              {totalStock > 0 ? 'En stock' : 'Sin stock disponible'}
            </span>
          </div>
          <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
            {branches.length} {branches.length === 1 ? 'sucursal' : 'sucursales'}
          </span>
        </div>

        {/* Branch list */}
        <div className="overflow-y-auto max-h-80 divide-y divide-zinc-50">
          {branches.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center px-5">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-zinc-100 mb-3">
                <i className="ri-store-3-line text-zinc-400 text-xl" />
              </div>
              <p className="text-zinc-500 text-sm font-medium">Sin disponibilidad registrada</p>
              <p className="text-zinc-400 text-xs mt-1">Consulta por WhatsApp para más info</p>
            </div>
          ) : (
            branches.map((branch) => (
              <BranchRow key={branch.branchId} branch={branch} />
            ))
          )}
        </div>

        {/* Footer actions */}
        <div className="px-5 py-4 border-t border-zinc-100 flex gap-3">
          <a
            href={`https://wa.me/${API_CONFIG.WHATSAPP_NUMBER ?? '18492081166'}?text=${encodeURIComponent(
              `Hola, quiero saber disponibilidad de: ${fullProductName(product)}. ¿Pueden ayudarme?\n${productPageUrl(API_CONFIG.COMPANY_ID, product.productId)}`
            )}`}
            target="_blank"
            rel="nofollow noreferrer"
            className="flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-extrabold uppercase tracking-widest rounded-full bg-emerald-500 hover:bg-emerald-600 text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line" />
            Consultar por WhatsApp
          </a>
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider rounded-xl border border-zinc-200 text-zinc-600 hover:border-zinc-400 hover:text-zinc-900 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function BranchRow({ branch }: { branch: BranchAvailabilityGroup }) {
  return (
    <div className="px-5 py-3.5 hover:bg-zinc-50 transition-colors">
      {/* Branch header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-zinc-100 flex-shrink-0">
            <i className="ri-store-2-line text-zinc-500 text-sm" />
          </div>
          <div className="min-w-0">
            <p className="text-zinc-900 font-semibold text-sm leading-tight truncate">
              {branch.branchName}
            </p>
            <p className="text-[10px] text-zinc-400 mt-0.5">
              {branch.warehouses.length} {branch.warehouses.length === 1 ? 'almacén' : 'almacenes'}
            </p>
          </div>
        </div>
        <div className="text-right flex-shrink-0 ml-3">
          {branch.minPrice > 0 && (
            <p className="text-zinc-900 font-extrabold text-sm">
              RD${branch.minPrice.toLocaleString('es-DO')}
            </p>
          )}
          <p
            className={`text-[10px] font-medium ${
              branch.totalQuantity > 0 ? 'text-emerald-600' : 'text-red-500'
            }`}
          >
            {branch.totalQuantity > 0 ? 'En stock' : 'Sin stock'}
          </p>
        </div>
      </div>

      {/* Warehouses */}
      {branch.warehouses.length > 1 && (
        <div className="ml-10.5 space-y-1.5 border-l border-zinc-100 pl-3 mt-1">
          {branch.warehouses.map((w, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5 min-w-0">
                <span
                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    w.quantity > 0 ? 'bg-emerald-400' : 'bg-red-300'
                  }`}
                />
                <span className="text-zinc-600 truncate">
                  {w.warehouseName || 'Almacén'}
                  {w.stockLocation && (
                    <span className="text-zinc-400 ml-1">({w.stockLocation})</span>
                  )}
                  {w.primaryIdentifier && (
                    <span className="text-zinc-300 ml-1">· {w.primaryIdentifier}</span>
                  )}
                </span>
              </div>
              <span className="text-zinc-500 flex-shrink-0 ml-2">
                RD${w.price.toLocaleString('es-DO')}
              </span>
            </div>
          ))}
        </div>
      )}

      {branch.warehouses.length === 1 && branch.warehouses[0].stockLocation && (
        <p className="ml-10.5 text-[10px] text-zinc-400 mt-1 pl-3 border-l border-zinc-100">
          Ubicación: {branch.warehouses[0].stockLocation}
          {branch.warehouses[0].primaryIdentifier && (
            <span className="ml-1">· {branch.warehouses[0].primaryIdentifier}</span>
          )}
        </p>
      )}
    </div>
  );
}